import { useEffect, useRef, useState } from "react";

interface CurseRejectionOverlayProps {
  active: boolean;
  onComplete: () => void;
}

// 呪いのメッセージが画面に浮かぶ
const CURSE_WHISPERS = [
  { text: "資格がない", x: "15%", y: "12%", delay: 0 },
  { text: "お前は、知らない", x: "72%", y: "8%", delay: 300 },
  { text: "ここは、お前の場所ではない", x: "8%", y: "35%", delay: 600 },
  { text: "去れ", x: "85%", y: "28%", delay: 200 },
  { text: "知らぬ者は還れ", x: "60%", y: "55%", delay: 500 },
  { text: "境界は拒絶する", x: "5%", y: "62%", delay: 800 },
  { text: "NO COVENANT", x: "78%", y: "72%", delay: 400 },
  { text: "不浄なる者よ", x: "30%", y: "80%", delay: 700 },
  { text: "†", x: "50%", y: "22%", delay: 100 },
  { text: "†", x: "20%", y: "50%", delay: 900 },
  { text: "†", x: "88%", y: "45%", delay: 350 },
];

const GLITCH_CHARS = "アイウエオカキ†‡§#@!░▒▓█";

function glitchString(str: string, intensity: number): string {
  return str
    .split("")
    .map((c) =>
      Math.random() < intensity
        ? GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)]
        : c
    )
    .join("");
}

export default function CurseRejectionOverlay({
  active,
  onComplete,
}: CurseRejectionOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  // フェーズ: whisper → condemn → collapse → closing → void
  const [phase, setPhase] = useState<
    "whisper" | "condemn" | "collapse" | "closing" | "void"
  >("whisper");
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const [mainText, setMainText] = useState("お前には資格がない");
  const [visibleWhispers, setVisibleWhispers] = useState<number[]>([]);
  const [pageScale, setPageScale] = useState(1);
  const [pageOpacity, setPageOpacity] = useState(1);

  // フェーズタイムライン
  useEffect(() => {
    if (!active) return;

    setPhase("whisper");
    setVisibleWhispers([]);
    setPageScale(1);
    setPageOpacity(1);

    // 囁きを順番に出現
    CURSE_WHISPERS.forEach((_, i) => {
      setTimeout(
        () => setVisibleWhispers((prev) => [...prev, i]),
        400 + i * 180
      );
    });

    // condemn フェーズ
    const t1 = setTimeout(() => setPhase("condemn"), 2600);
    // collapse フェーズ
    const t2 = setTimeout(() => setPhase("collapse"), 4200);
    // closing：ページが縮んで消えていく
    const t3 = setTimeout(() => {
      setPhase("closing");
      setPageScale(0.85);
      setPageOpacity(0.6);
    }, 5600);
    const t4 = setTimeout(() => {
      setPageScale(0.6);
      setPageOpacity(0.2);
    }, 6400);
    const t5 = setTimeout(() => {
      setPageScale(0.3);
      setPageOpacity(0);
    }, 7000);
    // void：虚無画面
    const t6 = setTimeout(() => setPhase("void"), 7500);
    // 完了コールバック
    const t7 = setTimeout(() => onComplete(), 10000);

    return () => {
      [t1, t2, t3, t4, t5, t6, t7].forEach(clearTimeout);
    };
  }, [active, onComplete]);

  // メインテキストのグリッチ
  useEffect(() => {
    if (phase !== "condemn" && phase !== "collapse") return;
    const base = "お前には資格がない";
    const interval = setInterval(() => {
      const intensity = phase === "collapse" ? 0.55 : 0.3;
      setMainText(glitchString(base, intensity));
    }, 90);
    return () => clearInterval(interval);
  }, [phase]);

  // Canvas：呪いのノイズ・血の滲み
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    let t = 0;

    function draw() {
      t++;
      const p = phaseRef.current;
      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      // ─ ベース暗幕 ─
      const bgAlpha =
        p === "whisper" ? Math.min(t * 0.005, 0.75)
          : p === "condemn" ? 0.85
          : p === "collapse" ? 0.92
          : p === "closing" ? 0.96
          : 1.0;
      ctx.fillStyle = `rgba(0, 0, 0, ${bgAlpha})`;
      ctx.fillRect(0, 0, w, h);

      // ─ 血の滲み（condemn以降） ─
      if (p === "condemn" || p === "collapse" || p === "closing") {
        const bloodCount = p === "collapse" ? 8 : 4;
        for (let i = 0; i < bloodCount; i++) {
          const bx = Math.random() * w;
          const by = Math.random() * h;
          const br = 40 + Math.random() * 120;
          const ba = 0.04 + Math.random() * 0.08;
          const bloodGrd = ctx.createRadialGradient(bx, by, 0, bx, by, br);
          bloodGrd.addColorStop(0, `rgba(160, 0, 0, ${ba})`);
          bloodGrd.addColorStop(0.5, `rgba(100, 0, 0, ${ba * 0.5})`);
          bloodGrd.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = bloodGrd;
          ctx.beginPath();
          ctx.arc(bx, by, br, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // ─ スキャンライン（collapse） ─
      if (p === "collapse" || p === "closing") {
        const lineCount = p === "closing" ? 20 : 8;
        for (let i = 0; i < lineCount; i++) {
          const ly = Math.random() * h;
          const lh = 1 + Math.random() * 4;
          ctx.fillStyle = `rgba(180, 0, 0, ${0.15 + Math.random() * 0.2})`;
          ctx.fillRect(0, ly, w, lh);
        }
      }

      // ─ 四隅から迫る黒の侵食（closing） ─
      if (p === "closing" || p === "void") {
        const cornerSize = p === "void" ? w : (t - 300) * 3;
        // 左上
        const c1 = ctx.createRadialGradient(0, 0, 0, 0, 0, cornerSize);
        c1.addColorStop(0, "rgba(0,0,0,0.9)");
        c1.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = c1;
        ctx.fillRect(0, 0, cornerSize, cornerSize);
        // 右下
        const c2 = ctx.createRadialGradient(w, h, 0, w, h, cornerSize);
        c2.addColorStop(0, "rgba(0,0,0,0.9)");
        c2.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = c2;
        ctx.fillRect(w - cornerSize, h - cornerSize, cornerSize, cornerSize);
      }

      // ─ void：完全な虚無 ─
      if (p === "void") {
        ctx.fillStyle = "rgba(0,0,0,1)";
        ctx.fillRect(0, 0, w, h);
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [active]);

  if (!active) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden">
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ width: "100%", height: "100%" }}
      />

      {/* ── フェーズ1：囁きが画面に散らばる ── */}
      {(phase === "whisper" || phase === "condemn") &&
        CURSE_WHISPERS.map((w, i) => (
          <div
            key={i}
            className="absolute pointer-events-none"
            style={{
              left: w.x,
              top: w.y,
              opacity: visibleWhispers.includes(i) ? 1 : 0,
              transform: visibleWhispers.includes(i)
                ? "translateY(0) scale(1)"
                : "translateY(8px) scale(0.9)",
              transition: `opacity 0.6s ease ${w.delay}ms, transform 0.6s ease ${w.delay}ms`,
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: w.text === "†" ? "1.4rem" : "0.65rem",
              letterSpacing: w.text === "†" ? "0" : "0.25em",
              fontStyle: "italic",
              color:
                w.text === "†"
                  ? "rgba(150,0,0,0.5)"
                  : "rgba(200,60,60,0.35)",
              textShadow: "0 0 12px rgba(150,0,0,0.3)",
              whiteSpace: "nowrap",
              animation:
                phase === "condemn" && visibleWhispers.includes(i)
                  ? "whisperFloat 3s ease-in-out infinite"
                  : "none",
            }}
          >
            {w.text}
          </div>
        ))}

      {/* ── フェーズ2：メイン宣告文 ── */}
      {(phase === "condemn" || phase === "collapse" || phase === "closing") && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{
            transform: `scale(${pageScale})`,
            opacity: pageOpacity,
            transition:
              phase === "closing"
                ? "transform 0.8s cubic-bezier(0.2,0,0,1), opacity 0.8s ease"
                : "none",
          }}
        >
          <div className="text-center px-8">
            {/* 上の十字 */}
            <div
              className="flex items-center justify-center mb-6"
              style={{
                opacity: phase === "condemn" ? 1 : 0.4,
                transition: "opacity 0.8s ease",
              }}
            >
              <div
                className="w-16 h-px"
                style={{ background: "linear-gradient(to right, transparent, rgba(150,0,0,0.4))" }}
              />
              <span
                style={{
                  color: "rgba(150,0,0,0.5)",
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.2rem",
                  margin: "0 12px",
                }}
              >
                †
              </span>
              <div
                className="w-16 h-px"
                style={{ background: "linear-gradient(to left, transparent, rgba(150,0,0,0.4))" }}
              />
            </div>

            {/* メインメッセージ */}
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(2.2rem, 6vw, 4.5rem)",
                fontWeight: 300,
                color:
                  phase === "condemn"
                    ? "rgba(220,40,40,0.9)"
                    : "rgba(200,20,20,0.95)",
                textShadow:
                  "0 0 30px rgba(200,0,0,0.6), 0 0 60px rgba(150,0,0,0.3), 4px 0 0 rgba(0,0,0,0.5), -4px 0 0 rgba(0,0,0,0.5)",
                letterSpacing: "0.08em",
                lineHeight: 1.2,
                animation:
                  phase === "condemn"
                    ? "condemnEnter 0.8s cubic-bezier(0.1,0,0.1,1) forwards"
                    : phase === "collapse"
                    ? "condemnShake 0.1s ease-in-out infinite"
                    : "none",
              }}
            >
              {mainText}
            </div>

            {/* サブテキスト */}
            <div
              className="mt-6"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "0.65rem",
                letterSpacing: "0.5em",
                textTransform: "uppercase",
                color: "rgba(150,0,0,0.45)",
                opacity: phase === "condemn" ? 1 : phase === "collapse" ? 0.6 : 0.3,
                transition: "opacity 0.5s ease",
                animation: phase === "condemn" ? "subFadeIn 1s ease 0.6s both" : "none",
              }}
            >
              ACCESS DENIED · COVENANT REFUSED
            </div>

            {/* カウント（閉じていくカウント） */}
            {(phase === "collapse" || phase === "closing") && (
              <div
                className="mt-8"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "0.7rem",
                  letterSpacing: "0.35em",
                  color: "rgba(255,255,255,0.12)",
                  fontStyle: "italic",
                  animation: "closingPulse 0.8s ease-in-out infinite alternate",
                }}
              >
                {phase === "closing"
                  ? "接続が、切断される"
                  : "この扉は永遠に閉じる"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── フェーズ最終：void ── */}
      {phase === "void" && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ background: "rgba(0,0,0,1)" }}
        >
          <div
            className="text-center"
            style={{ animation: "voidReveal 1.5s ease forwards" }}
          >
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "0.6rem",
                letterSpacing: "0.8em",
                textTransform: "uppercase",
                color: "rgba(80,0,0,0.4)",
                marginBottom: "16px",
              }}
            >
              CONNECTION TERMINATED
            </div>
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "0.55rem",
                letterSpacing: "0.4em",
                color: "rgba(255,255,255,0.06)",
                fontStyle: "italic",
              }}
            >
              再び資格を得た時、扉は開かれる
            </div>
            <div
              className="mt-8 flex items-center justify-center gap-2"
              style={{ opacity: 0.15 }}
            >
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: "3px",
                    height: "3px",
                    borderRadius: "50%",
                    background: "rgba(150,0,0,0.5)",
                    animation: `voidDot 1.2s ease-in-out ${i * 0.4}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes whisperFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.35; }
          33% { transform: translateY(-4px) rotate(-0.5deg); opacity: 0.5; }
          66% { transform: translateY(3px) rotate(0.3deg); opacity: 0.28; }
        }

        @keyframes condemnEnter {
          0% {
            opacity: 0;
            transform: scale(1.4) translateY(-20px);
            filter: blur(8px);
          }
          60% {
            opacity: 1;
            transform: scale(0.97) translateY(4px);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
            filter: blur(0);
          }
        }

        @keyframes condemnShake {
          0%, 100% { transform: translateX(0) skewX(0); }
          20% { transform: translateX(-4px) skewX(-1.5deg); }
          40% { transform: translateX(4px) skewX(1.5deg); }
          60% { transform: translateX(-2px) skewX(-0.8deg); }
          80% { transform: translateX(2px) skewX(0.8deg); }
        }

        @keyframes subFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes closingPulse {
          from { opacity: 0.08; }
          to { opacity: 0.2; }
        }

        @keyframes voidReveal {
          0% { opacity: 0; transform: translateY(8px); }
          40% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes voidDot {
          0%, 100% { transform: scale(1); opacity: 0.15; }
          50% { transform: scale(1.8); opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
